import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout/Layout";
import { SearchIcon, StarIcon } from "@heroicons/react/solid";
import CustomerServices from "../../services/CustomerServices";
import { formatDistanceToNow } from "date-fns";
import Paginate from "../../utils/Paginate";
import DatePicker from "react-date-picker";
import { formatDate } from "../../utils/convertDate";
import fire from "../../utils/Toastr";

const Ratings = () => {
  const [select, setSelect] = useState(-1);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [firstnames, setFirstNames] = useState([]);
  const [lastnames, setLastNames] = useState([]);
  const [modeStars, setModeStars] = useState(null);
  const [search, setSearch] = useState("");
  const [reviews, setReviews] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [fromm, setFromm] = useState("");
  const [too, setToo] = useState("");
  const [formValues, setFormValues] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 7)),
    to: new Date(),
  });
  const [exportLoading, setExportLoading] = useState(false);

  const exportRatings = async () => {
    setExportLoading(true);
    try {
      const resp = await CustomerServices.exportRatings(
        "PremiumMobile",
        pageIndex,
        pageSize,
        fromm,
        too
      );
      if (resp?.data?.url) {
        fire("success", resp.message || "Export successful");
        const link = document.createElement("a");
        link.href = resp.data.url;
        link.download = "CustomersReviewExport.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        fire("error", resp?.message || "Export failed - no URL returned");
      }
    } catch (error) {
      console.error("Error exporting ratings:", error);
      fire("error", "Failed to export ratings");
    } finally {
      setExportLoading(false);
    }
  };

  const getRatings = async () => {
    try {
      const response = await CustomerServices.GetReviews(
        "PremiumMobile",
        pageIndex,
        pageSize,
        fromm,
        too
      );

      if (response) {
        setReviews(response?.data.result || []);
        setFilteredReviews(response?.data.result || []);
        setTotalCount(response?.data.totalCount || 0);
        setTotalPages(response?.data.totalPages || 0);
        setHasNextPage(response?.data.hasNextPage || false);
        setHasPreviousPage(response?.data.hasPreviousPage || false);

        // Extract and split full names
        const names = response?.data.result.map((review) => {
          const [firstname, lastname] = (review.customerName || "").split(" ");
          return { firstname, lastname };
        });

        setFirstNames(names.map((name) => name.firstname));
        setLastNames(names.map((name) => name.lastname));

        // Calculate median stars for all reviews (not just current page)
        const allReviewsResponse = await CustomerServices.GetReviews(
          "PremiumMobile",
          1,
          10,
          fromm,
          too
        );
        const allReviews = allReviewsResponse?.data.result || [];
        const medianStars = calculateMedianStars(allReviews);
        setModeStars(medianStars);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const dateValidation = {
    fromHandler: (event) => {
      setFormValues({ ...formValues, from: event });
    },
    toHandler: (event) => {
      setFormValues({ ...formValues, to: event });
    },
  };

  const handleDateFilter = (e) => {
    e.preventDefault();
    const newDate = formatDate(formValues.from);
    const longDate = formatDate(newDate);
    setFromm(longDate);

    const toDate = formatDate(formValues.to);
    setToo(toDate);

    setPageIndex(1);
    getRatings();
  };

  const clearDateFilter = () => {
    setFormValues({
      from: new Date(new Date().setDate(new Date().getDate() - 7)),
      to: new Date(),
    });
    setFromm("");
    setToo("");
    setPageIndex(1);
    getRatings();
  };

  const selectCategory = (index) => {
    setSelect(index);
    if (index === -1) {
      setFilteredReviews(reviews);
    } else {
      const filtered = reviews.filter(
        (review) => review.numberOfStars === index
      );
      setFilteredReviews(filtered);
      applyFilters(search, index);
    }
  };

  const getNumberofStarsPerReview = (num) => {
    const numStars = reviews.filter((x) => x.numberOfStars === num).length;
    return numStars;
  };

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearch(value);
    applyFilters(value, select);
  };

  const applyFilters = (searchTerm, starRating) => {
    let filtered = reviews;

    if (starRating !== -1) {
      filtered = filtered.filter(
        (review) => review.numberOfStars === starRating
      );
    }

    if (searchTerm) {
      filtered = filtered.filter((review) =>
        review.customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredReviews(filtered);
  };

  const calculateMedianStars = (reviews) => {
    if (!reviews || reviews.length === 0) return 0;

    const starCounts = reviews.map((review) => review.numberOfStars);
    starCounts.sort((a, b) => a - b);
    const middleIndex = Math.floor(starCounts.length / 2);

    if (starCounts.length % 2 === 0) {
      return (starCounts[middleIndex - 1] + starCounts[middleIndex]) / 2;
    } else {
      return starCounts[middleIndex];
    }
  };

  useEffect(() => {
    getRatings();
  }, [pageIndex, pageSize]);

  return (
    <Layout
      title={"Customer Reviews"}
      pageTag={"customer"}
      current={"/customer/reviews"}
    >
      <React.Fragment>
        {/* Rating Summary Section */}
        <div className="w-full py-5">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex-1">
              <h1 className="text-4xl font-semibold">
                {modeStars?.toFixed(1) || "0.0"}
              </h1>
              <div className="flex items-center mt-3">
                {[1, 2, 3].map((_, i) => (
                  <span key={i}>
                    <StarIcon
                      className={`lg:h-5 md:w-5 w-4 h-4 ${
                        modeStars && i < Math.round(modeStars)
                          ? "text-yellow-400"
                          : "text-gray-400"
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                ))}
              </div>
              <p className="text-gray-500">{totalCount} Reviews</p>
            </div>

            {/* Rating Distribution Graph */}
            <div className="flex-1 w-full">
              <div className="flex flex-col gap-3">
                {[1, 2, 3].map((i) => {
                  const count = getNumberofStarsPerReview(i);
                  const percentage =
                    totalCount > 0 ? (count / totalCount) * 100 : 0;

                  return (
                    <div className="flex items-center gap-4" key={i}>
                      <p className="w-6 text-right">{i}</p>
                      <div className="w-full relative h-2 rounded-full bg-gray-300">
                        <div
                          className="absolute h-full bg-black rounded-full"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <p className="w-16 text-left">{percentage.toFixed(1)}%</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            {/* Search Input */}
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Reviews
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="text"
                  name="user"
                  id="user"
                  value={search}
                  onChange={handleSearch}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md h-10"
                  placeholder="Search by customer name"
                />
              </div>
            </div>

            {/* Date Filters */}
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <DatePicker
                    onChange={dateValidation.fromHandler}
                    value={formValues.from}
                    className="w-full border-gray-300 rounded-md text-sm h-10"
                    clearIcon={null}
                    calendarIcon={null}
                  />
                </div>
                <span className="text-gray-500">to</span>
                <div className="flex-1">
                  <DatePicker
                    onChange={dateValidation.toHandler}
                    value={formValues.to}
                    className="w-full border-gray-300 rounded-md text-sm h-10"
                    clearIcon={null}
                    calendarIcon={null}
                  />
                </div>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleDateFilter}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-slate-800 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 h-10"
              >
                Apply
              </button>
              {(formValues.from || formValues.to) && (
                <button
                  onClick={clearDateFilter}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 h-10"
                >
                  Clear
                </button>
              )}
              <button
                onClick={exportRatings}
                disabled={exportLoading}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>

        {/* Rating Filter Tabs */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => selectCategory(-1)}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                select === -1
                  ? "bg-red-100 text-red-700 border border-red-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Reviews
            </button>
            {[1, 2, 3].map((i) => (
              <button
                key={i}
                onClick={() => selectCategory(i)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  select === i
                    ? "bg-red-100 text-red-700 border border-red-300"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {i} Star{i !== 1 ? "s" : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid lg:grid-cols-3 grid-cols-1 md:grid-cols-2 gap-6">
          {filteredReviews.length > 0 ? (
            filteredReviews.map((item, i) => (
              <div
                key={item?.reviewID}
                className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-full h-12 w-12 bg-gray-100 flex items-center justify-center text-gray-600 font-medium">
                    {firstnames[i]?.[0]}
                    {lastnames[i]?.[0]}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-gray-900">
                        {item?.customerName}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(item?.createdOn), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      {[1, 2, 3].map((_, j) => (
                        <StarIcon
                          key={j}
                          className={`h-5 w-5 ${
                            j < item?.numberOfStars
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="mt-3 text-gray-600 text-sm">
                      {item?.comment}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-10 text-gray-500">
              No reviews to show
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredReviews.length > 0 && (
          <div className="mt-8">
            <Paginate
              nextPage={hasNextPage}
              pageIndex={pageIndex}
              totalCount={totalCount}
              totalPages={totalPages}
              prevPage={hasPreviousPage}
              setPageIndex={setPageIndex}
            />
          </div>
        )}
      </React.Fragment>
    </Layout>
  );
};

export default Ratings;
